import { generateRoomCode, sanitizeRoomCode } from './roomCode.js';
import type { NetworkConnectionState, NetworkMessage, OnlineGameMode, PeerRole } from './types';

// Default WebSocket endpoint (can be overridden via localStorage or window.__WS_URL__)
function getWebSocketUrl(room: string, role: PeerRole, mode: OnlineGameMode): string {
  let base = 'wss://findsets-backend.roeypeleg.workers.dev/ws';

  if (typeof window !== 'undefined') {
    const custom = localStorage.getItem('WS_SERVER_URL') || (window as any).__WS_URL__;
    if (custom) {
      base = custom.startsWith('http')
        ? custom.replace(/^http/, 'ws')
        : custom;
      if (!base.endsWith('/ws')) {
        base = base.replace(/\/+$/, '') + '/ws';
      }
    }
  }

  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}room=${encodeURIComponent(room)}&role=${encodeURIComponent(role)}&mode=${encodeURIComponent(mode)}`;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private role: PeerRole | null = null;
  private mode: OnlineGameMode | null = null;
  private roomCode: string | null = null;
  private state: NetworkConnectionState = 'idle';

  private messageHandlers: Set<(msg: NetworkMessage) => void> = new Set();
  private stateHandlers: Set<(state: NetworkConnectionState, error?: string) => void> = new Set();

  private heartbeatInterval: number | null = null;
  private joinTimeoutTimer: number | null = null;
  private lastStartGameMsg: { mode: OnlineGameMode; seed: string } | null = null;
  private disconnectReason: string | null = null;

  public getState(): NetworkConnectionState {
    return this.state;
  }

  public getRole(): PeerRole | null {
    return this.role;
  }

  public getMode(): OnlineGameMode | null {
    return this.mode;
  }

  public getRoomCode(): string | null {
    return this.roomCode;
  }

  public getClockOffset(): number {
    return 0; // Authoritative server has zero clock skew
  }

  public setLastStartGameMsg(msg: { mode: OnlineGameMode; seed: string } | null): void {
    this.lastStartGameMsg = msg;
  }

  public getLastStartGameMsg(): { mode: OnlineGameMode; seed: string } | null {
    return this.lastStartGameMsg;
  }

  public onMessage(handler: (msg: NetworkMessage) => void): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  public onStateChange(handler: (state: NetworkConnectionState, error?: string) => void): () => void {
    this.stateHandlers.add(handler);
    return () => this.stateHandlers.delete(handler);
  }

  private setState(state: NetworkConnectionState, error?: string): void {
    this.state = state;
    this.stateHandlers.forEach((handler) => handler(state, error));
  }

  /**
   * Host creates a new room with a random 4-letter code.
   */
  public async createRoom(mode: OnlineGameMode): Promise<string> {
    this.cleanup();
    this.mode = mode;
    this.role = 'host';

    const code = generateRoomCode();
    this.roomCode = code;
    this.setState('creating_room');

    return new Promise<string>((resolve, reject) => {
      const url = getWebSocketUrl(code, 'host', mode);

      try {
        const ws = new WebSocket(url);
        this.ws = ws;

        ws.onopen = () => {
          console.log('[WSClient] Connected to room as Host:', code);
          this.setState('waiting_for_guest');
          this.startHeartbeat();
          resolve(code);
        };

        ws.onmessage = (event) => {
          this.handleIncomingData(event.data);
        };

        ws.onclose = () => {
          console.log('[WSClient] Host connection closed');
          this.handleDisconnect();
        };

        ws.onerror = (err) => {
          console.warn('[WSClient] Host connection error:', err);
          this.setState('error', 'Could not connect to multiplayer server');
          reject(err);
        };
      } catch (err) {
        this.setState('error', 'WebSocket initialization failed');
        reject(err);
      }
    });
  }

  /**
   * Guest joins an existing room using the 4-letter code.
   */
  public async joinRoom(mode: OnlineGameMode, rawCode: string): Promise<void> {
    this.cleanup();
    const code = sanitizeRoomCode(rawCode);
    if (code.length !== 4) {
      throw new Error('Room code must be 4 characters');
    }

    this.mode = mode;
    this.role = 'guest';
    this.roomCode = code;
    this.setState('joining_room');

    // 15-second connection timeout
    this.clearJoinTimeout();
    this.joinTimeoutTimer = window.setTimeout(() => {
      if (this.state === 'joining_room') {
        console.warn('[WSClient] Room connection timed out');
        this.setState('error', 'Connection timed out. Please check the room code or your internet connection.');
        this.cleanup();
      }
    }, 15000);

    return new Promise<void>((resolve, reject) => {
      const url = getWebSocketUrl(code, 'guest', mode);

      try {
        const ws = new WebSocket(url);
        this.ws = ws;

        ws.onopen = () => {
          console.log('[WSClient] Connected to room as Guest:', code);
          this.clearJoinTimeout();
          this.startHeartbeat();
          resolve();
        };

        ws.onmessage = (event) => {
          this.handleIncomingData(event.data);
        };

        ws.onclose = () => {
          console.log('[WSClient] Guest connection closed');
          this.handleDisconnect();
        };

        ws.onerror = (err) => {
          console.warn('[WSClient] Guest connection error:', err);
          this.clearJoinTimeout();
          this.setState('error', 'Could not connect to room. Please check the code.');
          reject(err);
        };
      } catch (err) {
        this.clearJoinTimeout();
        this.setState('error', 'WebSocket initialization failed');
        reject(err);
      }
    });
  }

  private handleIncomingData(data: unknown): void {
    if (!data) return;
    try {
      const raw = typeof data === 'string' ? data : new TextDecoder().decode(data as ArrayBuffer);
      const msg = JSON.parse(raw) as NetworkMessage;

      // Internal protocol handling
      if (msg.type === 'PING') {
        this.send({ type: 'PONG' });
        return;
      }
      if (msg.type === 'PONG') {
        return;
      }

      // Room lifecycle transitions
      if (msg.type === 'OPPONENT_JOINED') {
        console.log('[WSClient] Opponent connected to room');
        this.setState('connected');
      } else if (msg.type === 'START_GAME') {
        this.setState('connected');
      } else if (msg.type === 'LEAVE_MATCH' || msg.type === 'OPPONENT_DISCONNECTED') {
        console.log('[WSClient] Opponent left match');
        this.disconnectReason = 'Opponent left the match (Victory by forfeit!)';
        this.setState('disconnected', 'Opponent left the match (Victory by forfeit!)');
      } else if (msg.type === 'ERROR') {
        this.setState('error', msg.message);
      }

      // Broadcast to application message handlers
      this.messageHandlers.forEach((handler) => {
        try {
          handler(msg);
        } catch (err) {
          console.error('[WSClient] Error in message handler:', err);
        }
      });
    } catch (err) {
      console.error('[WSClient] Error parsing message:', err);
    }
  }

  public send(msg: NetworkMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(msg));
      } catch (err) {
        console.warn('[WSClient] Failed to send message:', err);
      }
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({ type: 'PING' });
      }
    }, 5000);
  }

  private handleDisconnect(): void {
    if (this.state === 'connected' || this.state === 'waiting_for_guest') {
      const reason = this.disconnectReason || 'Opponent left the match';
      this.setState('disconnected', reason);
    }
  }

  private clearJoinTimeout(): void {
    if (this.joinTimeoutTimer) {
      clearTimeout(this.joinTimeoutTimer);
      this.joinTimeoutTimer = null;
    }
  }

  public cleanup(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    this.clearJoinTimeout();

    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }

    this.role = null;
    this.mode = null;
    this.roomCode = null;
    this.lastStartGameMsg = null;
    this.disconnectReason = null;
    this.setState('idle');
  }
}

// Global singleton instance for application use
export const wsClient = new WebSocketClient();
// Backward compatibility alias for existing references
export const p2pClient = wsClient;
export { wsClient as client };
